import { Component, OnInit } from '@angular/core';
import { dateFormatter } from '../../utils';
import { Store } from '@ngrx/store';
import { AppState } from '../../store';
import * as fromProcess from '../../store/process/process.reducer';
import * as fromRemote from '../../store/remote/remote.reducer';
import { BehaviorSubject, Observable, first, map, combineLatest } from 'rxjs';
import { FormsService } from '../../services/forms.service';
import { PersistStorageKeys, PersistStorageService } from '../../services/persist-storage.service';
import { FormsPolicyholder, FormsResignation, FormsTravelInPl, FormsTripAbroad } from '../../model/forms.model';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { ProcessService } from '../../services/process.service';
import { TrackingService } from '../../tracking/tracking.service';
import { ProcessType } from 'src/app/model/process-type.model';
import { TrackingSuccessEvent } from 'src/app/tracking/model/success.model';

@Component({
  selector: 'app-page-success',
  templateUrl: './success.component.html',
  styleUrls: ['./success.component.scss'],
})
export class SuccessComponent implements OnInit {
  readonly processType = ProcessType;
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  isSuccess$ = this.store.select(fromProcess.selectProcessIsSuccess);
  summaryParams$ = new BehaviorSubject<{
    coverageDate: string;
    email: string;
  }>({ coverageDate: '—', email: '—' });
  clientEmail$ = this.store.select(fromRemote.selectClientEmail).pipe(map((email) => ({ email: email || '—' })));
  isClientEmail$ = this.clientEmail$.pipe(map((email) => email.email !== '—'));
  isForeigner$ = this.formsService.policyholder.controls.isForeigner.valueChanges.pipe(map((value) => !!value));
  remoteHashCode$ = this.store.select(fromRemote.selectHashCode);
  processHashCode$ = this.store.select(fromProcess.selectProcessHashCode);
  hashCode$ = combineLatest([this.processHashCode$, this.remoteHashCode$]).pipe(
    map(([processHashCode, remoteHashCode]) => remoteHashCode || processHashCode),
  );
  selectedPackage$ = this.store.select(fromProcess.selectOfferSelectedPackage);
  offerValue$ = this.store.select(fromProcess.selectOfferValue);
  selectProcessSavedOfferPolicyNumber$ = this.store.select(fromProcess.selectProcessSavedOfferPolicyNumber);

  constructor(
    private store: Store<AppState>,
    private formsService: FormsService,
    private persistStorage: PersistStorageService,
    private processService: ProcessService,
    private router: Router,
    private trackingService: TrackingService,
  ) {}

  get policyPdf$(): Observable<string | undefined> {
    return this.hashCode$.pipe(
      map((hashCode) => (hashCode ? `${environment.baseApiUrl}globapi/pub/policy/pdf?hashCode=${hashCode}` : undefined)),
      first(),
    );
  }

  ngOnInit() {
    this.store.dispatch(fromProcess.actions.setStep({ step: 7 }));
    this.processService.onPageInit();

    this.processKind$.subscribe((processKind) => {
      let dateFrom: string | null = null;
      const email: string | null =
        this.persistStorage.getJson<FormsPolicyholder>(PersistStorageKeys.FormsPolicyholder)?.emailAddress || null;

      switch (processKind) {
        case 'tripAbroad':
          dateFrom = this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.travelDates?.dateFrom || null;
          break;
        case 'travelInPL':
          dateFrom = this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.travelDates?.dateFrom || null;
          break;
        case 'resignation':
          dateFrom = this.persistStorage.getJson<FormsResignation>(PersistStorageKeys.FormsResignation)?.travelDates?.dateFrom || null;
          break;
      }

      this.summaryParams$.next({
        coverageDate: dateFrom ? dateFormatter(dateFrom) : '—',
        email: email || '—',
      });
    });

    this.implementTrackingBusinessEnd();
  }

  implementTrackingBusinessEnd() {
    let policyNumberValue: string | null;

    this.selectProcessSavedOfferPolicyNumber$.pipe(first()).subscribe((policyNumber) => {
      policyNumberValue = policyNumber ?? null;
    });

    combineLatest([this.processKind$, this.selectedPackage$, this.offerValue$])
      .pipe(first())
      .subscribe(([processKind, selectedPackage, offerValue]) => {
        let primaryCategory;
        switch (processKind) {
          case ProcessType.TRIPABROAD:
            primaryCategory = 'Travel';
            break;
          case ProcessType.TRAVELINPL:
            primaryCategory = 'Travel';
            break;
          case ProcessType.RESIGNATION:
            primaryCategory = 'Resignation';
            break;
          default:
            primaryCategory = null;
            break;
        }

        const summaryDetails: TrackingSuccessEvent = {
          policyNumber: policyNumberValue,
          primaryCategory,
          productName: selectedPackage?.name || null,
          productPrice: offerValue,
        };

        this.trackingService.businessEventEnd(summaryDetails);
      });
  }

  handleOffer(): void {
    this.store.dispatch(fromProcess.actions.setSuccess({ success: false }));

    void this.router.navigate(['page', 'step1']);
  }

  handleDownloadPolicy(): void {
    this.trackingService.downloadPolicy();
  }
}
